import { useState } from "react";
import { useLanguage } from "@/lib/i18n";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Globe } from "lucide-react";
import SEO from "@/components/SEO";

export default function ContactPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSending(true);
    try {
      await api.submitContact(form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO 
        title="Contact Pruva Jacuzzi Dubai - Get Your Free Quote"
        description="Contact Pruva Jacuzzi Dubai for premium spa and jacuzzi installations. Get a free site assessment and quote. Located in Deira, Dubai. Call +971 54 218 4086 or email info@pruvajacuzzi.com."
        keywords="contact pruva jacuzzi, jacuzzi quote dubai, spa installation dubai, jacuzzi dealer UAE, free spa consultation"
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/contact` : "https://www.pruvajacuzzi.com/contact"}
        breadcrumbs={[
          { name: "Home", url: typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com" },
          { name: "Contact Us", url: typeof window !== 'undefined' ? `${window.location.origin}/contact` : "https://www.pruvajacuzzi.com/contact" }
        ]}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28" style={{ background: "linear-gradient(135deg, #F8F9FA 0%, #F1F3F5 100%)" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t("contact.subtitle")}
          </p>
          <h1 data-testid="contact-title" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900">
            {t("contact.title")}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-5 gap-16 lg:gap-24">
            {/* Form */}
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3 block" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {t("contact.name")} *
                    </label>
                    <input data-testid="contact-name" name="name" value={form.name} onChange={handleChange} required
                      placeholder={t("contact.namePlaceholder")}
                      className="w-full border-0 border-b border-slate-300 rounded-none px-0 py-3 focus:outline-none focus:border-slate-800 bg-transparent text-slate-900 placeholder:text-slate-400 transition-colors duration-200"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3 block" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {t("contact.email")} *
                    </label>
                    <input data-testid="contact-email" name="email" type="email" value={form.email} onChange={handleChange} required
                      placeholder={t("contact.emailPlaceholder")}
                      className="w-full border-0 border-b border-slate-300 rounded-none px-0 py-3 focus:outline-none focus:border-slate-800 bg-transparent text-slate-900 placeholder:text-slate-400 transition-colors duration-200"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3 block" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {t("contact.phone")}
                    </label>
                    <input data-testid="contact-phone" name="phone" value={form.phone} onChange={handleChange}
                      placeholder={t("contact.phonePlaceholder")}
                      className="w-full border-0 border-b border-slate-300 rounded-none px-0 py-3 focus:outline-none focus:border-slate-800 bg-transparent text-slate-900 placeholder:text-slate-400 transition-colors duration-200"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3 block" style={{ fontFamily: "'Inter', sans-serif" }}>
                      {t("contact.subject")} *
                    </label>
                    <input data-testid="contact-subject" name="subject" value={form.subject} onChange={handleChange} required
                      placeholder={t("contact.subjectPlaceholder")}
                      className="w-full border-0 border-b border-slate-300 rounded-none px-0 py-3 focus:outline-none focus:border-slate-800 bg-transparent text-slate-900 placeholder:text-slate-400 transition-colors duration-200"
                      style={{ fontFamily: "'Inter', sans-serif" }} />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3 block" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {t("contact.message")} *
                  </label>
                  <textarea data-testid="contact-message" name="message" value={form.message} onChange={handleChange} required rows={5}
                    placeholder={t("contact.messagePlaceholder")}
                    className="w-full border-0 border-b border-slate-300 rounded-none px-0 py-3 focus:outline-none focus:border-slate-800 bg-transparent text-slate-900 placeholder:text-slate-400 resize-none transition-colors duration-200"
                    style={{ fontFamily: "'Inter', sans-serif" }} />
                </div>

                <button data-testid="contact-submit" type="submit" disabled={sending}
                  className="bg-slate-800 text-white px-10 py-4 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300">
                  {sending ? t("contact.sending") : t("contact.send")}
                </button>
              </form>
            </div>

            {/* Info */}
            <div className="lg:col-span-2">
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-medium text-slate-900 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {t("contact.company")}
                  </h3>
                </div>

                <div className="space-y-5">
                  <a href="tel:+971542184086" data-testid="contact-phone-link"
                    className="flex items-start gap-4 text-slate-600 hover:text-slate-800 transition-colors duration-200">
                    <Phone className="w-5 h-5 mt-0.5 shrink-0 text-slate-800" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{t("contact.phone")}</p>
                      <p className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>+971 54 218 4086</p>
                    </div>
                  </a>

                  <a href="mailto:info@pruvajacuzzi.com" data-testid="contact-email-link"
                    className="flex items-start gap-4 text-slate-600 hover:text-slate-800 transition-colors duration-200">
                    <Mail className="w-5 h-5 mt-0.5 shrink-0 text-slate-800" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{t("contact.email")}</p>
                      <p className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>info@pruvajacuzzi.com</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4 text-slate-600">
                    <MapPin className="w-5 h-5 mt-0.5 shrink-0 text-slate-800" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-slate-900">Location</p>
                      <p className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>{t("contact.location")}</p>
                    </div>
                  </div>

                  <a href="https://www.pruvajacuzzi.com" target="_blank" rel="noopener noreferrer"
                    className="flex items-start gap-4 text-slate-600 hover:text-slate-800 transition-colors duration-200">
                    <Globe className="w-5 h-5 mt-0.5 shrink-0 text-slate-800" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium text-slate-900">Website</p>
                      <p className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>www.pruvajacuzzi.com</p>
                      <p className="text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>www.pruvapools.com</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
